import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useOverflowMeasure } from './useOverflowMeasure';

/**
 * Affordances for a horizontal scroll row: edge state (canLeft/canRight) for the
 * fade indicators, click-and-drag/swipe to scroll, and vertical-wheel →
 * horizontal scroll. Spread `handlers` onto the scroll element and attach `ref`;
 * onClickCapture swallows the click that ends a drag so it never opens a card.
 */
export interface HorizontalScroll {
  ref: React.RefObject<HTMLDivElement>;
  canLeft: boolean;
  canRight: boolean;
  dragging: boolean;
  handlers: {
    onPointerDown: (e: React.PointerEvent) => void;
    onPointerMove: (e: React.PointerEvent) => void;
    onPointerUp: (e: React.PointerEvent) => void;
    onPointerCancel: (e: React.PointerEvent) => void;
    onClickCapture: (e: React.MouseEvent) => void;
  };
}

const DRAG_THRESHOLD = 6;

/** Provides edge state + drag/wheel handlers for a horizontal scroll row. */
export function useHorizontalScroll(): HorizontalScroll {
  const ref = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);
  const [dragging, setDragging] = useState(false);
  const drag = useRef({ down: false, moved: false, startX: 0, startLeft: 0 });

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setCanLeft(el.scrollLeft > 1);
    setCanRight(el.scrollLeft < max - 1);
  }, []);

  useOverflowMeasure(ref, update, [update]);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    el.addEventListener('scroll', update, { passive: true });

    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      const max = el.scrollWidth - el.clientWidth;
      if (max <= 0) return;
      const atStart = el.scrollLeft <= 0;
      const atEnd = el.scrollLeft >= max;
      if ((e.deltaY < 0 && atStart) || (e.deltaY > 0 && atEnd)) return;
      el.scrollLeft = Math.max(0, Math.min(el.scrollLeft + e.deltaY, max));
      e.preventDefault();
    };
    el.addEventListener('wheel', onWheel, { passive: false });

    return () => {
      el.removeEventListener('scroll', update);
      el.removeEventListener('wheel', onWheel);
    };
  }, [update]);

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    const el = ref.current;
    if (!el) return;
    drag.current = { down: true, moved: false, startX: e.clientX, startLeft: el.scrollLeft };
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const el = ref.current;
    const d = drag.current;
    if (!el || !d.down) return;
    const dx = e.clientX - d.startX;
    if (!d.moved && Math.abs(dx) < DRAG_THRESHOLD) return;
    if (!d.moved) {
      d.moved = true;
      setDragging(true);
      try {
        el.setPointerCapture(e.pointerId);
      } catch (_) {
        /* capture not supported */
      }
    }
    el.scrollLeft = d.startLeft - dx;
  };

  const endDrag = (e: React.PointerEvent) => {
    const el = ref.current;
    if (drag.current.down && el) {
      try {
        el.releasePointerCapture(e.pointerId);
      } catch (_) {
        /* wasn't captured */
      }
    }
    drag.current.down = false;
    setDragging(false);
  };

  const onClickCapture = (e: React.MouseEvent) => {
    if (drag.current.moved) {
      e.preventDefault();
      e.stopPropagation();
      drag.current.moved = false;
    }
  };

  return {
    ref,
    canLeft,
    canRight,
    dragging,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
      onClickCapture,
    },
  };
}

export default useHorizontalScroll;
